import { AppliedPromo, PaymentUiType, ValidationSource } from '../types/Model';
import { initialState } from './reducer';
import { ERROR_MESSAGE } from 'core-lib/constants/network';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { serializePromoValidate } from '../utils/serializer/promo';
import {
  fetchAutoApplyPromo,
  fetchLastUsed,
  fetchSummary,
  fetchWallet,
  validatePromoCode,
} from '../utils/methods';
import { isRetry } from '../utils/common';
import { initiatePayment, initiatePromoValidation, setPaymentData } from './action';

function parseErrorPopup(error: any) {
  if (error?.data?.meta_data?.error_bottom_sheet_details?.title) {
    return {
      title: error?.data?.meta_data?.error_bottom_sheet_details?.title,
      subTitle: error?.data?.meta_data?.error_bottom_sheet_details?.sub_title,
      cta: {
        title: error?.data?.meta_data?.error_bottom_sheet_details?.cta_details?.title,
        webUrl: error?.data?.meta_data?.error_bottom_sheet_details?.cta_details?.web_url,
      },
    };
  }
  return null;
}

function parseAppliedPromo({ data, wallet }) {
  return {
    code: data.code,
    discount: data.calculated_discount,
    discountBreakup: data.discount_distribution,
    msg: data.alert_message,
    description: data.description,
    isCashback: data.cashback,
    showPromoCodeSavings: data?.show_promocode_savings ?? true,
    cashbackMsg: data.cashback_description,
    walletAccepted: data.wallet_accepted,
    walletErrMsg: wallet.isApplied
      ? data?.wallet_accepted_messages?.confirmation_message
      : data?.wallet_accepted_messages?.error_message || null,
    uiMeta: {
      title: data.ui_meta?.title,
      subtitle: data.ui_meta?.subtitle,
      staticText: data.ui_meta?.static_text,
      ctaText: data.ui_meta?.cta_text,
    },
    rawData: data,
  };
}

function showEligiblePaymentMethodsForPromoPopup({ validatedPromo, error }) {
  return {
    isPromoValidating: initialState.isPromoValidating,
    wasLastPromoSuccessful: initialState.wasLastPromoSuccessful,
    showMethodPopup: true,
    validatedPromo: validatedPromo,
    invalidPromoMsg: error.message,
  };
}

function setMethodAsSelectedPaymentMethodForPromo(method: any) {
  return {
    isPromoValidating: initialState.isPromoValidating,
    wasLastPromoSuccessful: false,
    selectedMethod: method,
    showMethodChangeTooltip: true,
  };
}
function handleError(
  validationSource: string | null,
  error: any,
  validatedPromo = null,
  msg = null,
) {
  const promoListElement = document.getElementById('promolist');
  const promoErrorPopup = parseErrorPopup(error);
  const invalidPromoMsg = msg || error?.message || ERROR_MESSAGE.genericFailure;
  const data: any = {
    invalidPromoMsg,
    promoErrorPopup,
  };
  if (error?.message) {
    if (!validationSource) {
      data.showMethodPopup = false;
      data.appliedPromo = validatedPromo;
      data.showPromoPopup = false;
    }

    if (promoListElement) promoListElement.scrollIntoView();
  }
  return data;
}
function* handlePromoValidationError(
  error: any,
  amount: number,
  validationSource: string | null,
  showMethodPopup: boolean,
) {
  if (!error.data) {
    return handleError(validationSource, error);
  }
  const validatedPromo = yield call(serializePromoValidate, error.data, amount);
  if (validationSource === ValidationSource.PromoPopUp) {
    const methods = validatedPromo.eligibleMethods.map(m => m.list).flat(1);
    if (methods.length === 1) {
      const method = methods[0];
      if (method.eligibilityResponse && !method?.eligibilityResponse?.eligible) {
        return handleError(
          validationSource,
          error,
          validatedPromo,
          method.eligibilityResponse.errorMessage,
        );
      } else if (method.uiType === PaymentUiType.Add) {
        return showEligiblePaymentMethodsForPromoPopup({
          validatedPromo,
          error,
        });
      } else {
        return setMethodAsSelectedPaymentMethodForPromo(method);
      }
    } else if (methods.length > 1 && !showMethodPopup) {
      return showEligiblePaymentMethodsForPromoPopup({ validatedPromo, error });
    } else {
      return handleError(validationSource, error, validatedPromo);
    }
  } else {
    return handleError(validationSource, error, validatedPromo);
  }
}

function handlePromoValidationResponse({ data, wallet, showSuccessPopup }) {
  const appliedPromo: AppliedPromo = parseAppliedPromo({ data, wallet });
  const result: any = {
    appliedPromo,
    showMethodPopup: initialState.showMethodPopup,
    showPromoPopup: initialState.showPromoPopup,
    invalidPromoMsg: initialState.invalidPromoMsg,
    isPromoValidating: initialState.isPromoValidating,
    wasLastPromoSuccessful: true,
    showPromoSuccess: !!showSuccessPopup,
  };
  if (!appliedPromo.walletAccepted && wallet.isApplied) {
    result.wallet = {
      ...wallet,
      isApplied: false,
    };
  }
  return result;
}

function* getWalletBalance() {
  const { origin, wallet, walletBalance, itemDetails } = yield select(state => state.paymentNew);
  if (wallet?.enabled && !walletBalance?.balance) {
    const walletRes = yield call(fetchWallet, { origin, itemDetails });
    if (walletRes) {
      yield put(setPaymentData({ ...walletRes }));
    }
  }
}
function* getLastUsed({ finalAmount }) {
  const { origin, itemDetails, selectedMethod } = yield select(state => state.paymentNew);
  const isCashOrder = origin?.data?.isCash;
  if (!isCashOrder && (!selectedMethod || selectedMethod?.eligibilityApiUrl)) {
    const lastUsedRes = yield call(fetchLastUsed, {
      origin,
      itemDetails,
      finalAmount,
    });
    if (lastUsedRes) {
      yield put(setPaymentData({ ...lastUsedRes }));
    }
  }
}
function* getUserPromo({ finalAmount }) {
  const {
    origin,
    amount,
    appliedPromo,
    itemDetails,
    promo,
    selectedMethod,
    wasLastPromoSuccessful,
    wallet,
    walletBalance,
    validatedPromo,
  } = yield select(state => state.paymentNew);
  if (!appliedPromo?.code && !validatedPromo?.code && promo?.autoApply && !wasLastPromoSuccessful) {
    return yield call(fetchAutoApplyPromo, {
      finalAmount,
      amount,
      origin,
      itemDetails,
      selectedMethod,
      wallet,
      walletBalance,
    });
  }
  return null;
}
function* getSummary({ finalAmount }) {
  const {
    origin,
    selectedMethod,
    wallet,
    walletBalance,
    itemDetails,
    promo,
    appliedPromo,
    invalidPromoMsg,
    tip,
    addOns,
    fullfilmentDetails,
  } = yield select(state => state.paymentNew);

  // Fetch summary
  const summaryRes = yield call(fetchSummary, {
    finalAmount,
    origin,
    itemDetails,
    promo,
    appliedPromo,
    invalidPromoMsg,
    tip,
    addOns,
    selectedMethod,
    wallet,
    walletBalance,
    fullfilmentDetails,
  });
  if (summaryRes) {
    yield put(setPaymentData({ ...summaryRes }));
  }
}
function* validatePromo({ finalAmount, code, validationSource = null, showSuccess = true }) {
  const {
    origin,
    amount,
    selectedMethod,
    wallet,
    walletBalance,
    itemDetails,
    appliedPromo,
    validatedPromo,
    wasLastPromoSuccessful,
    showMethodPopup,
  } = yield select(state => state.paymentNew);
  const promo = code ?? appliedPromo?.code ?? validatedPromo?.code;
  if (promo) {
    yield put(
      setPaymentData({
        isPromoValidating: true,
        invalidPromoMsg: initialState.invalidPromoMsg,
      }),
    );
    const { response, error } = yield call(validatePromoCode, {
      origin,
      amount,
      selectedMethod,
      wallet,
      walletBalance,
      itemDetails,
      finalAmount,
      code: promo,
    });
    let result: any;
    if (response?.data) {
      const data = response?.data;
      const showSuccessPopup =
        !isRetry() &&
        showSuccess &&
        (!wasLastPromoSuccessful || validationSource === ValidationSource.PromoPopUp);
      result = handlePromoValidationResponse({
        data,
        wallet,
        showSuccessPopup,
      });
    }
    if (error) {
      result = yield call(
        handlePromoValidationError,
        error,
        finalAmount,
        validationSource,
        showMethodPopup,
      );
    }

    yield put(setPaymentData({ ...result }));
  }
}

function* validatePromoSaga({ payload }) {
  const { finalAmount, code, validationSource, showSuccess } = payload;
  try {
    yield call(validatePromo, {
      finalAmount,
      code,
      validationSource,
      showSuccess,
    });
  } catch (error) {
    console.error('Failed in initiateSaga', error);
  } finally {
    yield put(
      setPaymentData({
        isPromoValidating: initialState.isPromoValidating,
      }),
    );
  }
}
function* initiateSaga({ payload }) {
  const finalAmount = payload;
  try {
    yield put(setPaymentData({ isPaymentReady: false }));
    yield call(getWalletBalance);
    yield call(getLastUsed, { finalAmount });
    const code = yield call(getUserPromo, { finalAmount });
    yield call(validatePromo, { finalAmount, code });
    yield call(getSummary, { finalAmount });
  } catch (error) {
    console.error('Failed in initiateSaga', error);
  } finally {
    yield put(
      setPaymentData({
        isPaymentReady: true,
        isPaymentInitiated: true,
        isPromoValidating: initialState.isPromoValidating,
      }),
    );
  }
}

export function* watchPayment() {
  yield takeLatest(initiatePayment, initiateSaga);
  yield takeLatest(initiatePromoValidation, validatePromoSaga);
}
